import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DesktopHeaderCategoryItem } from './DesktopHeaderCategories.types';
import { getDesktopHeaderCategories } from './DesktopHeaderCatalogue.helpers';
import { useCategories } from '@/hooks/useCategories';
import { ALL_SHOPS_ID } from '@/components/CatalogueGrid/CatalogueGrid.constants';
import { ROUTES } from '@/constants/routes';
import { hasCategoryType } from '@/utils/helpers';
import { Category } from '@/api/types/categories.types';

export const useDesktopHeaderCategories = (isOpen: boolean) => {
  const { categories: items } = useCategories(isOpen);
  const { t } = useTranslation();
  const shopItem: DesktopHeaderCategoryItem = {
    id: ALL_SHOPS_ID,
    path: ROUTES.shops,
    name: t('catalogue.stores'),
    iconSrc: '/images/shop.png',
    hasDecendants: false,
  };
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedSubItemId, setSelectedSubItemId] = useState<string | null>(
    null
  );
  const [selectedPrimaryPropertyOptionId, setSelectedPrimaryPropertyOptionId] =
    useState<string | null>(null);

  const selectedItem = useMemo(
    () => items.find(({ id }) => id === selectedItemId),
    [items, selectedItemId]
  );

  const subItems = useMemo(
    () =>
      (selectedItem?.children?.length
        ? selectedItem.children
        : selectedItem?.propertyOptions) || [],
    [selectedItem]
  );

  const selectedSubItem = useMemo(
    () =>
      subItems.find(
        subItem => hasCategoryType(subItem) && subItem.id === selectedSubItemId
      ) as Category | undefined,
    [subItems, selectedSubItemId]
  );

  const primaryProperty = useMemo(
    () => selectedSubItem?.primaryProperty,
    [selectedSubItem]
  );

  return {
    items: [...getDesktopHeaderCategories(items), shopItem],
    selectedItemId,
    setSelectedItemId,
    subItems: getDesktopHeaderCategories(subItems, selectedItem),
    selectedSubItemId,
    setSelectedSubItemId,
    primaryPropertyOptions: getDesktopHeaderCategories(
      primaryProperty?.options || [],
      selectedSubItem,
      primaryProperty?.legacyResourceId || ''
    ),
    selectedPrimaryPropertyOptionId,
    setSelectedPrimaryPropertyOptionId,
  };
};
